import type { TranslationKey } from '~/shared/internationalization/translation';

import { useGlobalPermissions } from '../../query-hooks/global-permissions';
import { useJobArchitectureOptionsQuery } from '../../query-hooks/job-architecture-options';

type TabKey = 'employees' | 'jobLevels' | 'jobRoles' | 'jobs' | 'learningOpportunities' | 'skills';

export type TabConfig = {
    readonly key: TabKey;
    readonly translationKey: TranslationKey;
};

const availableTabs: Record<TabKey, TabConfig> = {
    employees: {
        key: 'employees',
        translationKey: 'pages.global-search.navigation.employees',
    },
    jobs: {
        key: 'jobs',
        translationKey: 'pages.global-search.navigation.job-openings',
    },
    jobLevels: {
        key: 'jobLevels',
        translationKey: 'pages.global-search.navigation.job-levels',
    },
    jobRoles: {
        key: 'jobRoles',
        translationKey: 'pages.global-search.navigation.job-roles',
    },
    learningOpportunities: {
        key: 'learningOpportunities',
        translationKey: 'pages.global-search.navigation.learning-opportunities',
    },
    skills: {
        key: 'skills',
        translationKey: 'pages.global-search.navigation.skills',
    },
};

export const useFilterTabsByPermission = ({
    includeJobArchitectureTabs = false,
}: {
    readonly includeJobArchitectureTabs?: boolean;
} = {}): readonly TabConfig[] => {
    const { data: globalPermissions } = useGlobalPermissions();
    const jobArchitectureOptions = useJobArchitectureOptionsQuery({ enabled: includeJobArchitectureTabs });
    const canUserSeeSkillTab = globalPermissions?.canSearchSkills ?? false;
    const canUserSeeEmployeesTab = globalPermissions?.canSearchUsers ?? false;
    const canUserSeeJobsTab = globalPermissions?.canSearchJobs ?? false;
    const canUserSeeLearningOpportunitiesTab = globalPermissions?.canSearchLearningOpportunities ?? false;
    const canUserSeeJobArchitectureTabs =
        includeJobArchitectureTabs && jobArchitectureOptions.data?.isOk === true
            ? jobArchitectureOptions.data.value.canUserGetJobArchitecture
            : false;

    const permittedTabs: TabConfig[] = [];

    if (canUserSeeSkillTab) {
        permittedTabs.push(availableTabs.skills);
    }

    if (canUserSeeEmployeesTab) {
        permittedTabs.push(availableTabs.employees);
    }

    if (canUserSeeJobsTab) {
        permittedTabs.push(availableTabs.jobs);
    }

    if (canUserSeeJobArchitectureTabs) {
        permittedTabs.push(availableTabs.jobRoles);
        permittedTabs.push(availableTabs.jobLevels);
    }

    if (canUserSeeLearningOpportunitiesTab) {
        permittedTabs.push(availableTabs.learningOpportunities);
    }

    return permittedTabs;
};
